import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, Loader2, Send, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export function EquityQuestionsInbox({ companyId }) {
  const [data, setData] = useState(null);
  const [showAnswered, setShowAnswered] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/companies/${companyId}/equity-questions`).then(({ data }) => setData(data)).catch(() => setData({ rows: [], open: 0 }));
  useEffect(() => { if (companyId) load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = async (q) => {
    const text = (drafts[q.id] || "").trim();
    if (!text) { toast.error("Write an answer first"); return; }
    setBusy(q.id);
    try {
      const { data: res } = await api.post(`/equity-questions/${q.id}/answer`, { answer: text });
      toast.success(res.emailed ? `Answered - emailed to ${q.holder_name}` : "Answered (no email on file)");
      setDrafts((d) => ({ ...d, [q.id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not answer"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const rows = data.rows.filter((q) => showAnswered || q.status === "open");
  if (data.rows.length === 0) return null;
  return (
    <Card id="equity-questions" className="scroll-mt-24 rounded-xl border bg-card p-5" data-testid="equity-questions-inbox">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessageCircleQuestion className="h-4 w-4 text-primary" /> Employee equity questions
          {data.open > 0 && <Badge className="border-amber-200 bg-amber-50 font-normal text-amber-800" data-testid="equity-questions-open-count">{data.open} open</Badge>}
        </h2>
        <button type="button" onClick={() => setShowAnswered((v) => !v)} className="text-xs font-semibold text-primary" data-testid="equity-questions-toggle-answered">{showAnswered ? "Hide answered" : "Show answered"}</button>
      </div>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground" data-testid="equity-questions-none-open">All caught up - no open questions.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((q) => (
            <li key={q.id} className="rounded-lg border p-3" data-testid={`equity-inbox-${q.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span><span className="font-semibold text-foreground">{q.holder_name}</span>{q.holder_email ? ` · ${q.holder_email}` : ""} · {formatDateTime(q.created_at)}</span>
                {q.status === "answered" && <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> answered {formatDateTime(q.answered_at)}</span>}
              </div>
              <p className="mt-1.5 text-sm text-foreground">{q.question}</p>
              {q.status === "answered" ? (
                <div className="mt-2 whitespace-pre-wrap rounded-md border-l-2 border-primary bg-secondary/50 p-2.5 text-sm text-foreground">{q.answer}</div>
              ) : (
                <div className="mt-2 space-y-2">
                  <Textarea rows={3} value={drafts[q.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))} placeholder="Write your answer - it's emailed to the employee and shown on their equity page." className="text-sm" data-testid={`equity-answer-input-${q.id}`} />
                  <div className="flex justify-end"><Button size="sm" disabled={busy === q.id} onClick={() => answer(q)} className="gap-1.5" data-testid={`equity-answer-submit-${q.id}`}>{busy === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send answer</Button></div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
